// Обработка микрофона TESTCS — рабочий Web Audio-тракт для экрана
// VOICE ENHANCEMENT.
//
// Принцип экрана: это ОЧИСТКА, а не подмена голоса. Мы не клонируем и не
// синтезируем речь — только режем шум, гасим гул комнаты и правим тембр
// фильтрами. Поэтому в графе нет ни одного узла, меняющего высоту или
// характер голоса: только гейт, биквады, компрессор, лимитер и громкость.
//
// Граф:
//   getUserMedia → source ─┬─ analyserOriginal        (сухой сигнал для A/B)
//                          └─ gate → lowCut → warmth → clarity → deEsser
//                             → compressor → limiter → outputGain
//                             → analyserEnhanced → destination
//
// Наружу отдаём два MediaStream: сухой и обработанный. Обработанный можно
// слушать, писать MediaRecorder'ом и отдавать в LiveKit.
//
// Честная карта «ползунок → что реально происходит» — в mapSettings():
// семь параметров из девяти continuous и считаются в графе, а Noise
// Suppression и Room Echo Reduction дополнительно включают штатные
// браузерные NS/AEC (они булевы — процент управляет нашим гейтом).

const STORAGE_KEY = 'testcs.voice.v1'
const PRESETS_KEY = 'testcs.voice.presets.v1'

// ─── Параметры тракта (эталонный лист: значения и рекомендованные диапазоны) ──

export const VOICE_PARAMS = [
  {
    id: 'noiseSuppression',
    label: 'Noise Suppression',
    ru: 'Шумоподавление',
    unit: '%',
    min: 0,
    max: 100,
    step: 1,
    rec: [10, 30],
    recLabel: '10–30%',
    does: 'порог шумового гейта + штатный NS браузера',
  },
  {
    id: 'echoReduction',
    label: 'Room Echo Reduction',
    ru: 'Гул комнаты',
    unit: '%',
    min: 0,
    max: 100,
    step: 1,
    rec: [10, 40],
    recLabel: '10–40%',
    does: 'скорость закрытия гейта (хвост комнаты) + штатный AEC',
  },
  {
    id: 'lowCut',
    label: 'Low-Cut Filter',
    ru: 'Обрезка низа',
    unit: 'Hz',
    min: 40,
    max: 200,
    step: 5,
    rec: [80, 120],
    recLabel: '80–120 Hz',
    does: 'highpass-биквад: гул, стол, ветер',
  },
  {
    id: 'clarityEq',
    label: 'Clarity EQ',
    ru: 'Разборчивость',
    unit: 'dB',
    min: 0,
    max: 6,
    step: 0.5,
    rec: [1, 3],
    recLabel: '+1…+3 dB',
    signed: true,
    does: 'peaking +dB на 3.2 кГц — согласные',
  },
  {
    id: 'warmth',
    label: 'Warmth',
    ru: 'Теплота',
    unit: '%',
    min: 0,
    max: 100,
    step: 1,
    rec: [10, 30],
    recLabel: '10–30%',
    does: 'lowshelf до +5 dB на 220 Гц — тело голоса',
  },
  {
    id: 'deEsser',
    label: 'De-Esser',
    ru: 'Смягчение «с»',
    unit: '%',
    min: 0,
    max: 100,
    step: 1,
    rec: [10, 30],
    recLabel: '10–30%',
    does: 'peaking до −10 dB на 6.8 кГц — свист шипящих',
  },
  {
    id: 'compressor',
    label: 'Compressor (Light)',
    ru: 'Компрессор',
    unit: '%',
    min: 0,
    max: 100,
    step: 1,
    rec: [10, 30],
    recLabel: '10–30%',
    does: 'ровность громкости: порог и ratio',
  },
  {
    id: 'limiter',
    label: 'Limiter',
    ru: 'Лимитер',
    unit: 'dB',
    min: 0,
    max: 6,
    step: 0.5,
    rec: [0, 3],
    recLabel: '0–3 dB',
    does: 'потолок пиков, ratio 20:1',
  },
  {
    id: 'outputGain',
    label: 'Output Gain',
    ru: 'Выходной уровень',
    unit: 'dB',
    min: -12,
    max: 6,
    step: 0.5,
    rec: [-3, 0],
    recLabel: '−3…0 dB',
    signed: true,
    does: 'итоговая громкость отправляемого голоса',
  },
]

export const DEFAULT_SETTINGS = {
  noiseSuppression: 20,
  echoReduction: 25,
  lowCut: 100,
  clarityEq: 2,
  warmth: 20,
  deEsser: 20,
  compressor: 20,
  limiter: 1,
  outputGain: -1,
}

// Штатные браузерные фильтры на самом захвате. Автогромкость по умолчанию
// выключена: она дерётся с нашим компрессором за один и тот же сигнал.
export const DEFAULT_CAPTURE = { echoCancellation: true, noiseSuppression: true, autoGainControl: false }

export const VOICE_PRESETS = [
  { id: 'natural', label: 'NATURAL', hint: 'Базовая очистка, ничего лишнего', values: { ...DEFAULT_SETTINGS } },
  {
    id: 'quiet',
    label: 'QUIET ROOM',
    hint: 'Тихая комната, хороший микрофон',
    values: { noiseSuppression: 10, echoReduction: 10, lowCut: 80, clarityEq: 1.5, warmth: 25, deEsser: 15, compressor: 15, limiter: 1, outputGain: -1 },
  },
  {
    id: 'noisy',
    label: 'NOISY ROOM',
    hint: 'Шум вокруг: улица, вентилятор, соседи',
    values: { noiseSuppression: 45, echoReduction: 40, lowCut: 120, clarityEq: 2.5, warmth: 15, deEsser: 25, compressor: 30, limiter: 2, outputGain: -1.5 },
  },
  {
    id: 'stream',
    label: 'STREAM',
    hint: 'Стрим и запись: плотнее и ровнее',
    values: { noiseSuppression: 30, echoReduction: 25, lowCut: 100, clarityEq: 3, warmth: 25, deEsser: 25, compressor: 35, limiter: 2.5, outputGain: -1 },
  },
]

// ─── Ползунок → физика тракта ────────────────────────────────────────────────

export function mapSettings(s) {
  return {
    // 0% — гейт фактически выключен (порог ниже любого микрофона),
    // 100% — режет всё тише −33 dBFS
    gateThresholdDb: -75 + (clamp01(s.noiseSuppression / 100) * 42),
    gateEnabled: s.noiseSuppression > 0,
    // чем сильнее «гул комнаты», тем быстрее закрывается гейт — короче хвост.
    // Нижняя граница жёсткая: 0.02 — минимум AudioParam у гейта, ниже
    // значение всё равно зажмётся, но уже молча.
    gateRelease: Math.max(0.02, 0.3 - clamp01(s.echoReduction / 100) * 0.28),
    captureNoiseSuppression: s.noiseSuppression > 0,
    captureEchoCancellation: s.echoReduction > 0,
    lowCutHz: s.lowCut,
    warmthDb: clamp01(s.warmth / 100) * 5,
    clarityDb: s.clarityEq,
    deEsserDb: -clamp01(s.deEsser / 100) * 10,
    compRatio: 1 + clamp01(s.compressor / 100) * 6,
    compThresholdDb: -10 - clamp01(s.compressor / 100) * 20,
    limitThresholdDb: -s.limiter,
    outputGainLin: Math.pow(10, s.outputGain / 20),
  }
}

function clamp01(v) {
  return Math.max(0, Math.min(1, Number.isFinite(v) ? v : 0))
}

export function clampParam(param, value) {
  const v = Number(value)
  if (!Number.isFinite(v)) return param.min
  return Math.max(param.min, Math.min(param.max, v))
}

export function formatParamValue(param, value) {
  const v = Number(value) || 0
  if (param.unit === 'Hz') return `${Math.round(v)} Hz`
  if (param.unit === 'dB') {
    const sign = param.signed && v > 0 ? '+' : ''
    return `${sign}${v.toFixed(1)} dB`
  }
  return `${Math.round(v)}%`
}

// В рекомендованном диапазоне? Экран подсвечивает выход за него.
export function isRecommended(param, value) {
  const v = Number(value)
  return v >= param.rec[0] && v <= param.rec[1]
}

export function matchPreset(settings, presets = VOICE_PRESETS) {
  return (
    presets.find(preset =>
      VOICE_PARAMS.every(param => Math.abs(Number(settings[param.id]) - Number(preset.values[param.id])) < 0.001)
    ) || null
  )
}

export function normalizeSettings(raw) {
  const out = { ...DEFAULT_SETTINGS }
  if (!raw || typeof raw !== 'object') return out
  for (const param of VOICE_PARAMS) {
    if (raw[param.id] !== undefined) out[param.id] = clampParam(param, raw[param.id])
  }
  return out
}

// ─── Хранение настроек ───────────────────────────────────────────────────────

export function loadVoiceSettings() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')
    if (!raw) return { settings: { ...DEFAULT_SETTINGS }, enabled: true, deviceId: '', capture: { ...DEFAULT_CAPTURE } }
    return {
      settings: normalizeSettings(raw.settings),
      enabled: raw.enabled !== false,
      deviceId: typeof raw.deviceId === 'string' ? raw.deviceId : '',
      capture: { ...DEFAULT_CAPTURE, ...(raw.capture || {}) },
    }
  } catch {
    return { settings: { ...DEFAULT_SETTINGS }, enabled: true, deviceId: '', capture: { ...DEFAULT_CAPTURE } }
  }
}

export function saveVoiceSettings({ settings, enabled, deviceId, capture }) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ settings: normalizeSettings(settings), enabled: enabled !== false, deviceId: deviceId || '', capture: { ...DEFAULT_CAPTURE, ...(capture || {}) } })
    )
    return true
  } catch {
    return false
  }
}

export function loadCustomPresets() {
  try {
    const raw = JSON.parse(localStorage.getItem(PRESETS_KEY) || '[]')
    if (!Array.isArray(raw)) return []
    return raw
      .filter(item => item && typeof item.label === 'string')
      .map(item => ({ id: item.id || `custom-${item.label}`, label: item.label, hint: 'Свой пресет', custom: true, values: normalizeSettings(item.values) }))
  } catch {
    return []
  }
}

export function saveCustomPreset(label, settings) {
  const clean = String(label || '').trim().slice(0, 24)
  if (!clean) return loadCustomPresets()
  const list = loadCustomPresets().filter(item => item.label.toLowerCase() !== clean.toLowerCase())
  const next = [...list, { id: `custom-${Date.now()}`, label: clean, hint: 'Свой пресет', custom: true, values: normalizeSettings(settings) }].slice(-8)
  try {
    localStorage.setItem(PRESETS_KEY, JSON.stringify(next))
  } catch {
    /* приватный режим браузера — пресет живёт до перезагрузки */
  }
  return next
}

export function deleteCustomPreset(id) {
  const next = loadCustomPresets().filter(item => item.id !== id)
  try {
    localStorage.setItem(PRESETS_KEY, JSON.stringify(next))
  } catch {
    /* см. выше */
  }
  return next
}

// ─── Шумовой гейт (AudioWorklet из blob — без отдельного файла в сборке) ─────
//
// Классический downward-гейт с раздельными атакой и восстановлением.
// Он же считает, какую долю времени реально приглушает сигнал — это честная
// замена «CPU %» из макета: показываем работу шумодава, а не выдуманную цифру.

const GATE_WORKLET_SOURCE = `
class TestcsGate extends AudioWorkletProcessor {
  static get parameterDescriptors() {
    return [
      { name: 'threshold', defaultValue: -75, minValue: -100, maxValue: -10, automationRate: 'k-rate' },
      { name: 'release', defaultValue: 0.15, minValue: 0.02, maxValue: 0.8, automationRate: 'k-rate' },
      { name: 'enabled', defaultValue: 1, minValue: 0, maxValue: 1, automationRate: 'k-rate' },
    ]
  }

  constructor() {
    super()
    this.env = 0
    this.gain = 1
    this.gainBuf = new Float32Array(128)
    this.quanta = 0
    this.ducked = 0
    this.total = 0
  }

  process(inputs, outputs, params) {
    const input = inputs[0]
    const output = outputs[0]
    if (!input || input.length === 0) return true

    const thr = Math.pow(10, params.threshold[0] / 20)
    const enabled = params.enabled[0] > 0.5
    const attackC = Math.exp(-1 / (0.004 * sampleRate))
    const releaseC = Math.exp(-1 / (Math.max(0.02, params.release[0]) * sampleRate))
    const envC = Math.exp(-1 / (0.02 * sampleRate))

    const mono = input[0]
    const frames = mono.length
    if (this.gainBuf.length !== frames) this.gainBuf = new Float32Array(frames)

    // огибающая и усиление считаются один раз по первому каналу
    for (let i = 0; i < frames; i++) {
      const a = Math.abs(mono[i])
      // быстро вверх, медленно вниз — иначе гейт «дышит» на согласных
      this.env = a > this.env ? a : this.env * envC + a * (1 - envC)
      const target = !enabled || this.env >= thr ? 1 : 0
      const c = target > this.gain ? attackC : releaseC
      this.gain = target + (this.gain - target) * c
      this.gainBuf[i] = this.gain
      if (this.gain < 0.95) this.ducked++
    }
    this.total += frames

    for (let ch = 0; ch < input.length; ch++) {
      const inp = input[ch]
      const out = output[ch]
      if (!inp || !out) continue
      for (let i = 0; i < frames; i++) out[i] = inp[i] * this.gainBuf[i]
    }

    // раз в ~32 мс отдаём в UI долю приглушённого времени
    if (++this.quanta >= 12) {
      this.port.postMessage({ duck: this.total ? this.ducked / this.total : 0 })
      this.quanta = 0
      this.ducked = 0
      this.total = 0
    }
    return true
  }
}
registerProcessor('testcs-gate', TestcsGate)
`

let gateModuleUrl = null

function gateWorkletUrl() {
  if (!gateModuleUrl) {
    gateModuleUrl = URL.createObjectURL(new Blob([GATE_WORKLET_SOURCE], { type: 'application/javascript' }))
  }
  return gateModuleUrl
}

async function addGateModule(ctx) {
  if (!ctx.audioWorklet) return false
  try {
    await ctx.audioWorklet.addModule(gateWorkletUrl())
    return true
  } catch {
    // старый браузер или заблокированный blob: тракт работает без гейта,
    // шумоподавление остаётся на штатном NS браузера
    return false
  }
}

// ─── Список микрофонов ───────────────────────────────────────────────────────

export async function listMicrophones() {
  if (!navigator.mediaDevices?.enumerateDevices) return []
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices
      .filter(d => d.kind === 'audioinput')
      .map((d, i) => ({ id: d.deviceId, label: d.label || `Микрофон ${i + 1}` }))
  } catch {
    return []
  }
}

// ─── Тракт ───────────────────────────────────────────────────────────────────

export class VoiceChain {
  constructor() {
    this.ctx = null
    this.stream = null
    this.source = null
    this.nodes = null
    this.gateNode = null
    this.analyserOriginal = null
    this.analyserEnhanced = null
    this.monitorGain = null
    this.enhancedDest = null
    this.originalDest = null
    this.settings = { ...DEFAULT_SETTINGS }
    this.capture = { ...DEFAULT_CAPTURE }
    this.bypass = false
    this.deviceId = ''
    this.duck = 0
    this.running = false
    this._timeBuf = null
  }

  get ready() {
    return this.running
  }

  // Обработанный поток — его слушают, пишут и отдают в LiveKit
  get enhancedStream() {
    return this.enhancedDest?.stream || null
  }

  // Сухой поток — только для A/B-сравнения
  get originalStream() {
    return this.originalDest?.stream || null
  }

  get sampleRate() {
    return this.ctx?.sampleRate || 0
  }

  // Реальная задержка тракта: захват + вывод + квант воркл ета
  get latencyMs() {
    if (!this.ctx) return 0
    const base = (this.ctx.baseLatency || 0) + (this.ctx.outputLatency || 0)
    const quantum = this.gateNode ? 128 / this.ctx.sampleRate : 0
    return (base + quantum) * 1000
  }

  async start({ deviceId = '', settings, capture } = {}) {
    if (this.running) await this.stop()
    if (settings) this.settings = normalizeSettings(settings)
    if (capture) this.capture = { ...DEFAULT_CAPTURE, ...capture }
    this.deviceId = deviceId || ''

    const mapped = mapSettings(this.settings)
    const constraints = {
      audio: {
        echoCancellation: this.capture.echoCancellation && mapped.captureEchoCancellation,
        noiseSuppression: this.capture.noiseSuppression && mapped.captureNoiseSuppression,
        autoGainControl: this.capture.autoGainControl,
        ...(this.deviceId ? { deviceId: { exact: this.deviceId } } : {}),
      },
      video: false,
    }

    this.stream = await navigator.mediaDevices.getUserMedia(constraints)
    const Ctx = window.AudioContext || window.webkitAudioContext
    this.ctx = new Ctx({ latencyHint: 'interactive' })
    if (this.ctx.state === 'suspended') await this.ctx.resume()

    const hasGate = await addGateModule(this.ctx)
    this.source = this.ctx.createMediaStreamSource(this.stream)

    // сухая ветка: анализатор + отдельный destination для записи «до»
    this.analyserOriginal = this.ctx.createAnalyser()
    this.analyserOriginal.fftSize = 2048
    this.analyserOriginal.smoothingTimeConstant = 0.6
    this.originalDest = this.ctx.createMediaStreamDestination()
    this.source.connect(this.analyserOriginal)
    this.source.connect(this.originalDest)

    // обработанная ветка
    if (hasGate) {
      this.gateNode = new AudioWorkletNode(this.ctx, 'testcs-gate')
      this.gateNode.port.onmessage = event => {
        if (typeof event.data?.duck === 'number') this.duck = event.data.duck
      }
    }

    const lowCut = this.ctx.createBiquadFilter()
    lowCut.type = 'highpass'
    lowCut.Q.value = 0.707

    const warmth = this.ctx.createBiquadFilter()
    warmth.type = 'lowshelf'
    warmth.frequency.value = 220

    const clarity = this.ctx.createBiquadFilter()
    clarity.type = 'peaking'
    clarity.frequency.value = 3200
    clarity.Q.value = 0.9

    const deEsser = this.ctx.createBiquadFilter()
    deEsser.type = 'peaking'
    deEsser.frequency.value = 6800
    deEsser.Q.value = 2.4

    const comp = this.ctx.createDynamicsCompressor()
    comp.knee.value = 24
    comp.attack.value = 0.01
    comp.release.value = 0.18

    const limiter = this.ctx.createDynamicsCompressor()
    limiter.knee.value = 0
    limiter.ratio.value = 20
    limiter.attack.value = 0.003
    limiter.release.value = 0.1

    const outputGain = this.ctx.createGain()

    this.analyserEnhanced = this.ctx.createAnalyser()
    this.analyserEnhanced.fftSize = 2048
    this.analyserEnhanced.smoothingTimeConstant = 0.6
    this.enhancedDest = this.ctx.createMediaStreamDestination()

    // Локальное прослушивание («послушать себя перед тем, как применить»).
    // По умолчанию наглухо закрыт: иначе с колонками сразу заведётся.
    this.monitorGain = this.ctx.createGain()
    this.monitorGain.gain.value = 0
    this.monitorGain.connect(this.ctx.destination)

    this.nodes = { lowCut, warmth, clarity, deEsser, comp, limiter, outputGain }
    this._connectChain()
    this.update(this.settings)
    this.running = true
    return this
  }

  _connectChain() {
    const { lowCut, warmth, clarity, deEsser, comp, limiter, outputGain } = this.nodes
    this.source.disconnect()
    this.source.connect(this.analyserOriginal)
    this.source.connect(this.originalDest)

    if (this.bypass) {
      // «Отключить обработку» — сырой микрофон идёт мимо всех узлов
      this.source.connect(this.analyserEnhanced)
      this.source.connect(this.enhancedDest)
      if (this.monitorGain) this.source.connect(this.monitorGain)
      return
    }

    const head = this.gateNode || lowCut
    this.source.connect(head)
    if (this.gateNode) this.gateNode.connect(lowCut)
    lowCut.connect(warmth)
    warmth.connect(clarity)
    clarity.connect(deEsser)
    deEsser.connect(comp)
    comp.connect(limiter)
    limiter.connect(outputGain)
    outputGain.connect(this.analyserEnhanced)
    outputGain.connect(this.enhancedDest)
    if (this.monitorGain) outputGain.connect(this.monitorGain)
  }

  // Слышать себя локально: наружу это не уходит, только в свои наушники.
  setMonitor(on) {
    if (!this.monitorGain || !this.ctx) return false
    this.monitorGain.gain.setTargetAtTime(on ? 1 : 0, this.ctx.currentTime, 0.02)
    return true
  }

  setBypass(flag) {
    const next = !!flag
    if (next === this.bypass || !this.running) {
      this.bypass = next
      return
    }
    this.bypass = next
    for (const node of Object.values(this.nodes)) node.disconnect()
    if (this.gateNode) this.gateNode.disconnect()
    this._connectChain()
    if (this.bypass) this.duck = 0
  }

  update(settings) {
    this.settings = normalizeSettings(settings)
    if (!this.running || !this.nodes) return this.settings
    const m = mapSettings(this.settings)
    const t = this.ctx.currentTime
    const ramp = 0.03 // плавно, иначе щелчки при таскании ползунка

    if (this.gateNode) {
      this.gateNode.parameters.get('threshold').setTargetAtTime(m.gateThresholdDb, t, ramp)
      this.gateNode.parameters.get('release').setTargetAtTime(m.gateRelease, t, ramp)
      this.gateNode.parameters.get('enabled').setValueAtTime(m.gateEnabled ? 1 : 0, t)
    }
    this.nodes.lowCut.frequency.setTargetAtTime(m.lowCutHz, t, ramp)
    this.nodes.warmth.gain.setTargetAtTime(m.warmthDb, t, ramp)
    this.nodes.clarity.gain.setTargetAtTime(m.clarityDb, t, ramp)
    this.nodes.deEsser.gain.setTargetAtTime(m.deEsserDb, t, ramp)
    this.nodes.comp.threshold.setTargetAtTime(m.compThresholdDb, t, ramp)
    this.nodes.comp.ratio.setTargetAtTime(m.compRatio, t, ramp)
    this.nodes.limiter.threshold.setTargetAtTime(m.limitThresholdDb, t, ramp)
    this.nodes.outputGain.gain.setTargetAtTime(m.outputGainLin, t, ramp)
    return this.settings
  }

  // Штатные NS/AEC/AGC живут на самом захвате — их меняем через applyConstraints
  async setCapture(capture) {
    this.capture = { ...DEFAULT_CAPTURE, ...capture }
    const track = this.stream?.getAudioTracks?.()[0]
    if (!track?.applyConstraints) return this.capture
    const m = mapSettings(this.settings)
    try {
      await track.applyConstraints({
        echoCancellation: this.capture.echoCancellation && m.captureEchoCancellation,
        noiseSuppression: this.capture.noiseSuppression && m.captureNoiseSuppression,
        autoGainControl: this.capture.autoGainControl,
      })
    } catch {
      // часть браузеров не даёт менять это на живом треке — переподключение
      // микрофона применит настройки
    }
    return this.capture
  }

  async setDevice(deviceId) {
    if (deviceId === this.deviceId && this.running) return
    await this.start({ deviceId, settings: this.settings, capture: this.capture })
  }

  // Уровень: RMS и пик в dBFS + линейная величина 0..1 для полоски
  levels(which = 'enhanced') {
    const analyser = which === 'original' ? this.analyserOriginal : this.analyserEnhanced
    if (!analyser) return { rmsDb: -100, peakDb: -100, linear: 0 }
    if (!this._timeBuf || this._timeBuf.length !== analyser.fftSize) {
      this._timeBuf = new Float32Array(analyser.fftSize)
    }
    analyser.getFloatTimeDomainData(this._timeBuf)
    let sum = 0
    let peak = 0
    for (let i = 0; i < this._timeBuf.length; i++) {
      const v = this._timeBuf[i]
      sum += v * v
      const a = Math.abs(v)
      if (a > peak) peak = a
    }
    const rms = Math.sqrt(sum / this._timeBuf.length)
    return {
      rmsDb: rms > 0 ? 20 * Math.log10(rms) : -100,
      peakDb: peak > 0 ? 20 * Math.log10(peak) : -100,
      linear: Math.min(1, rms * 4),
    }
  }

  // Столбики живой волны: bars значений 0..1
  waveform(which = 'enhanced', bars = 64) {
    const analyser = which === 'original' ? this.analyserOriginal : this.analyserEnhanced
    const out = new Array(bars).fill(0)
    if (!analyser) return out
    if (!this._timeBuf || this._timeBuf.length !== analyser.fftSize) {
      this._timeBuf = new Float32Array(analyser.fftSize)
    }
    analyser.getFloatTimeDomainData(this._timeBuf)
    const chunk = Math.floor(this._timeBuf.length / bars)
    for (let b = 0; b < bars; b++) {
      let peak = 0
      for (let i = b * chunk; i < (b + 1) * chunk; i++) {
        const a = Math.abs(this._timeBuf[i])
        if (a > peak) peak = a
      }
      out[b] = Math.min(1, peak * 2.2)
    }
    return out
  }

  async stop() {
    this.running = false
    try {
      this.stream?.getTracks?.().forEach(track => track.stop())
    } catch {
      /* поток уже мёртв */
    }
    try {
      if (this.gateNode) this.gateNode.port.onmessage = null
      await this.ctx?.close?.()
    } catch {
      /* контекст уже закрыт */
    }
    this.ctx = null
    this.stream = null
    this.source = null
    this.nodes = null
    this.gateNode = null
    this.analyserOriginal = null
    this.analyserEnhanced = null
    this.monitorGain = null
    this.enhancedDest = null
    this.originalDest = null
    this.duck = 0
  }
}

// ─── Оффлайн-прогон: импортированный файл через тот же тракт ────────────────
// Возвращает { original, enhanced } — два AudioBuffer для A/B.

export async function renderFileThroughChain(arrayBuffer, settings) {
  const Ctx = window.AudioContext || window.webkitAudioContext
  const decodeCtx = new Ctx()
  let decoded
  try {
    decoded = await decodeCtx.decodeAudioData(arrayBuffer.slice(0))
  } finally {
    decodeCtx.close?.()
  }

  const OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext
  const offline = new OfflineCtx(decoded.numberOfChannels, decoded.length, decoded.sampleRate)
  const hasGate = await addGateModule(offline)
  const m = mapSettings(normalizeSettings(settings))

  const source = offline.createBufferSource()
  source.buffer = decoded

  const lowCut = offline.createBiquadFilter()
  lowCut.type = 'highpass'
  lowCut.Q.value = 0.707
  lowCut.frequency.value = m.lowCutHz

  const warmth = offline.createBiquadFilter()
  warmth.type = 'lowshelf'
  warmth.frequency.value = 220
  warmth.gain.value = m.warmthDb

  const clarity = offline.createBiquadFilter()
  clarity.type = 'peaking'
  clarity.frequency.value = 3200
  clarity.Q.value = 0.9
  clarity.gain.value = m.clarityDb

  const deEsser = offline.createBiquadFilter()
  deEsser.type = 'peaking'
  deEsser.frequency.value = 6800
  deEsser.Q.value = 2.4
  deEsser.gain.value = m.deEsserDb

  const comp = offline.createDynamicsCompressor()
  comp.knee.value = 24
  comp.attack.value = 0.01
  comp.release.value = 0.18
  comp.threshold.value = m.compThresholdDb
  comp.ratio.value = m.compRatio

  const limiter = offline.createDynamicsCompressor()
  limiter.knee.value = 0
  limiter.ratio.value = 20
  limiter.attack.value = 0.003
  limiter.release.value = 0.1
  limiter.threshold.value = m.limitThresholdDb

  const outputGain = offline.createGain()
  outputGain.gain.value = m.outputGainLin

  let head = lowCut
  if (hasGate) {
    const gate = new AudioWorkletNode(offline, 'testcs-gate')
    gate.parameters.get('threshold').value = m.gateThresholdDb
    gate.parameters.get('release').value = m.gateRelease
    gate.parameters.get('enabled').value = m.gateEnabled ? 1 : 0
    gate.connect(lowCut)
    head = gate
  }

  source.connect(head)
  lowCut.connect(warmth)
  warmth.connect(clarity)
  clarity.connect(deEsser)
  deEsser.connect(comp)
  comp.connect(limiter)
  limiter.connect(outputGain)
  outputGain.connect(offline.destination)
  source.start()

  const enhanced = await offline.startRendering()
  return { original: decoded, enhanced }
}

// Пики для рисования волны по готовому AudioBuffer
export function bufferPeaks(buffer, bars = 96) {
  const out = new Array(bars).fill(0)
  if (!buffer) return out
  const data = buffer.getChannelData(0)
  const chunk = Math.max(1, Math.floor(data.length / bars))
  for (let b = 0; b < bars; b++) {
    let peak = 0
    const end = Math.min(data.length, (b + 1) * chunk)
    for (let i = b * chunk; i < end; i++) {
      const a = Math.abs(data[i])
      if (a > peak) peak = a
    }
    out[b] = Math.min(1, peak)
  }
  return out
}
